import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { CreateHealthCheckItemInput, HealthCheckItem, UpdateHealthCheckItemNameInput } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Health/PathItems';
import { HealthCheckItemManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import EditHealthCheckItem from './EditHealthCheckItem';
import AISuggestions from '../../components/AISuggestions';

const HealthCheckItemList: React.FC = () => {
  const { onError } = useToast();
  const [items, setItems] = useState<HealthCheckItem[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string, name: string } | null>(null);
  const [editingItem, setEditingItem] = useState<HealthCheckItem | null>(null);

  const loadItems = async () => {
    onError((await Session.getClient().request(HealthCheckItemManagement.getAllHealthCheckItems())).maybeOk((items) => setItems([...items])));
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDialogOpen = () => {
    setNewItemName('');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewItemName('');
  };

  const handleDialogOk = async () => {
    if (newItemName.trim()) {
      onError((await Session.getClient().request(HealthCheckItemManagement.createHealthCheckItem({ name: newItemName.trim() } as CreateHealthCheckItemInput)))
        .maybeOk(loadItems));
    }
    handleDialogClose();
  };

  const handleDeleteClick = (id: string, name: string) => {
    setItemToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteDialogOk = async () => {
    if (itemToDelete) {
      onError((await Session.getClient().addPathParam('healthCheckItemId', itemToDelete.id).request(HealthCheckItemManagement.deleteHealthCheckItem()))
        .maybeOk(loadItems));
    }
    handleDeleteDialogClose();
  };

  const handleEdit = (item: HealthCheckItem) => {
    setEditingItem(item);
  };

  const handleEditSave = async (name: string, translationKey: string) => {
    if (editingItem) {
      const client = Session.getClient().addPathParam('healthCheckItemId', editingItem.id);
      await onError((await client.request(HealthCheckItemManagement.updateHealthCheckItemName({ name } as UpdateHealthCheckItemNameInput))).maybeOk(loadItems));
      setEditingItem(null);
    }
  };

  if (editingItem) {
    return (
      <EditHealthCheckItem
        name={editingItem.name}
        translationKey={editingItem.translationKey}
        state={editingItem.state}
        onBack={() => setEditingItem(null)}
        onSave={handleEditSave}
        onPublishTransition={async (action) => {
          return (await Session.getClient().addPathParam('healthCheckItemId', editingItem.id).request(HealthCheckItemManagement.updateHealthCheckItemState({ stateTransition: action })))
            .maybeOk(() => {
              loadItems()
              setEditingItem(null);
            });
        }}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h6" gutterBottom>Health Check Items</Typography>
        <AISuggestions 
          client={Session.getClient()}
          currentSnapshot={JSON.stringify(items)}
          usageInstructions={HealthCheckItemManagement.toAgentInstructions()}
          developerMessage={'The user is in the view where they can see a list of health check items. For any of the operations ' +
            'regarding a specific item the path parameter with the name healthCheckItemId MUST be specified with that item id.'}
          onOperationsApplied={() => loadItems()}
        />
        <Button variant="contained" color="primary" onClick={handleDialogOpen}>New Item</Button>
      </Box>
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Add Health Check Item</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Item Name"
            fullWidth
            autoFocus
            value={newItemName}
            onChange={e => setNewItemName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Health Check Item</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this item?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteDialogOk} variant="contained" color="error">OK</Button>
        </DialogActions>
      </Dialog>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight="bold">Item Name</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Translation Key</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight="bold">Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => (
              <TableRow key={item.id}>
                <TableCell>
                  <Typography>{item.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{item.translationKey}</Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton edge="end" onClick={() => handleEdit(item)}><EditIcon /></IconButton>
                  <IconButton edge="end" color="error" onClick={() => handleDeleteClick(item.id, item.name)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default HealthCheckItemList;
